// wind-wall.ts — I1 H4: a loose object dropped into (or falling through) an active Wind Wall, decided in the browser.
//
// Pure twins of includes/functions/janus/encounter/painted_wall_wind_*.php: the lift-weight gate (≤ 5 lb, tiny/small,
// loose), the wall's apex over the object's footprint (volume apex), the swept entry of a straight move (span sweep →
// contact intervals → first entry), the clear apex under floors / roofs / occupancy, and the drop plan that picks the
// earliest interception (movement, release, then gravity). The destination-legality check a lift needs
// (ft_resolve_move_destination at the apex) and the obstacles are supplied by the caller — the adapter builds them from
// the page's roster with the golden-source move core. PHP persists the plan (the wind_wall_lift roll row + the settled
// position, then the fall's damage — H3) without re-deciding it.
import { toLocal, type Zone } from './floor-move.js';

type Row = Record<string, unknown>;
export const WIND_WALL_ID = 'B9032A2F-B68E-0D7E-A234-90CAD8959892';
/** FT_PAINTED_WIND_LOOSE_MAX_LB — the Throw Object lightweight bracket (mass, not force). */
export const WIND_LOOSE_MAX_LB = 5.0;
const SHAPE_EPS = 1e-6;
const TINY = '13456FEC-A2EE-DB46-5829-0EB2F627E1B7';
const SMALL = 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7';

export interface WindWallGeometry { points: { x: number; y: number; z: number }[]; heightFt: number; thicknessFt: number }

export interface LiftWeightInputs {
  possessId: string; encounterId: string; deleted: boolean; private: boolean; qty: number; equipped: boolean; immovable: boolean;
  mounted: boolean; itemType: string; sizeId: string | null;
  /** computed weight gross ?? computed total (null = absent), else the unit weight layer × qty. */
  grossLb: number | null; unitWeightLb: number | null;
}

/** ft_painted_wall_wind_lift_weight(): the whole loose row's weight, or null when the wind cannot tear it loose. */
export function liftWeight(input: LiftWeightInputs): number | null {
  if (input.possessId.toUpperCase() !== input.encounterId.toUpperCase() || input.deleted || input.private || input.qty < 1
    || input.equipped || input.immovable || input.mounted || ['wall', 'door', 'window', 'map_marker'].includes(input.itemType)) return null;
  const size = (input.sizeId ?? '').toUpperCase();
  if (![ '', TINY, SMALL ].includes(size)) return null;
  let weight = input.grossLb;
  if (weight === null) {
    if (input.unitWeightLb === null || !Number.isFinite(input.unitWeightLb)) return null;
    weight = input.unitWeightLb * Math.trunc(input.qty);
  }
  if (!Number.isFinite(weight) || weight < 0 || weight > WIND_LOOSE_MAX_LB) return null;
  return weight;
}

/** ft_shape_segment_interval_for_axis(). */
export function segmentIntervalForAxis(origin: number, delta: number, min: number, max: number): [number, number] | null {
  if (Math.abs(delta) < SHAPE_EPS) return origin >= min - SHAPE_EPS && origin <= max + SHAPE_EPS ? [-Infinity, Infinity] : null;
  const a = (min - origin) / delta, b = (max - origin) / delta;
  return [Math.min(a, b), Math.max(a, b)];
}

/** ft_painted_wind_segment_box_distance(): the nearest parameter on a segment to a centred axis-aligned rectangle. */
export function segmentBoxDistance(x: number, y: number, dx: number, dy: number, w: number, d: number, lo = 0, hi = 1): { t: number; distanceSquared: number } {
  const cuts = [lo, hi];
  for (const [origin, delta, extent] of [[x, dx, w], [y, dy, d]] as const) {
    if (Math.abs(delta) < 1e-12) continue;
    for (const edge of [-extent, extent]) { const t = (edge - origin) / delta; if (t > lo && t < hi) cuts.push(t); }
  }
  cuts.sort((a, b) => a - b);
  let best = Infinity, bestT = lo;
  for (let j = 1; j < cuts.length; j++) {
    const left = cuts[j - 1]!, right = cuts[j]!, mid = (left + right) / 2;
    let linear = 0, quadratic = 0;
    for (const [origin, delta, extent] of [[x, dx, w], [y, dy, d]] as const) {
      const v = origin + delta * mid;
      if (v < -extent || v > extent) { const offset = origin - (v < 0 ? -extent : extent); linear += offset * delta; quadratic += delta * delta; }
    }
    const t = quadratic > 1e-18 ? Math.max(left, Math.min(right, -linear / quadratic)) : mid;
    const distance = Math.max(0, Math.abs(x + dx * t) - w) ** 2 + Math.max(0, Math.abs(y + dy * t) - d) ** 2;
    if (distance < best) { best = distance; bestT = t; }
  }
  return { t: bestT, distanceSquared: best };
}

/** ft_painted_wall_wind_volume_apex(): the highest wall top touching the object's footprint above its feet, or null. */
export function volumeApex(wall: WindWallGeometry, volume: Zone): number | null {
  let apex: number | null = null;
  for (let i = 1; i < wall.points.length; i++) {
    const a = wall.points[i - 1]!, b = wall.points[i]!, dz = b.z - a.z;
    const interval = segmentIntervalForAxis(a.z, dz, volume.zMin - wall.heightFt - 0.001, volume.zMax + 0.001);
    if (!interval) continue;
    const lo = Math.max(0, interval[0]), hi = Math.min(1, interval[1]);
    if (lo > hi) continue;
    const circle = volume.kind === 'circle';
    let x: number, y: number, dx: number, dy: number, w: number, d: number;
    if (circle) { x = a.x - volume.x; y = a.y - volume.y; dx = b.x - a.x; dy = b.y - a.y; w = 0; d = 0; }
    else {
      [x, y] = toLocal(volume, a.x, a.y);
      const [bx, by] = toLocal(volume, b.x, b.y);
      dx = bx - x; dy = by - y; w = volume.halfW; d = volume.halfD;
    }
    const nearest = segmentBoxDistance(x, y, dx, dy, w, d, lo, hi);
    const reach = wall.thicknessFt / 2 + (circle ? volume.r : 0) + 0.001;
    if (nearest.distanceSquared > reach * reach) continue;
    const top = a.z + dz * nearest.t + wall.heightFt;
    if (top > volume.zMin + 0.001) apex = apex === null ? top : Math.max(apex, top);
  }
  return apex;
}

/** ft_painted_wind_span_sweep(): does a fixed footprint translated to `to` touch one convex wall span? */
export function spanSweep(wall: WindWallGeometry, volume: Zone, to: { x: number; y: number; z: number }): boolean {
  const a = wall.points[0]!, b = wall.points[1]!;
  const dx = to.x - volume.x, dy = to.y - volume.y, dz = to.z - volume.zMin;
  const wx = b.x - a.x, wy = b.y - a.y, wz = b.z - a.z;
  const qz = volume.zMin - a.z, height = volume.zMax - volume.zMin;
  let polygon: Array<[number, number]> = [[0, 0], [1, 0], [1, 1], [0, 1]];
  for (const [sign, bound] of [[1, wall.heightFt + 0.001], [-1, height + 0.001]] as const) {
    const output: Array<[number, number]> = [];
    polygon.forEach((p, i) => {
      const n = polygon[(i + 1) % polygon.length]!;
      const pd = sign * (qz + dz * p[0] - wz * p[1]) - bound;
      const nd = sign * (qz + dz * n[0] - wz * n[1]) - bound;
      if (pd <= 0) output.push(p);
      if ((pd <= 0) !== (nd <= 0)) { const t = pd / (pd - nd); output.push([p[0] + (n[0] - p[0]) * t, p[1] + (n[1] - p[1]) * t]); }
    });
    polygon = output;
    if (!polygon.length) return false;
  }
  const circle = volume.kind === 'circle';
  const angle = circle ? 0 : -volume.facing;
  const c = Math.cos(angle), s = Math.sin(angle);
  const relative = polygon.map(([t, u]) => {
    const x = a.x + wx * u - volume.x - dx * t, y = a.y + wy * u - volume.y - dy * t;
    return { x: x * c - y * s, y: x * s + y * c };
  });
  let area = 0, positive = false, negative = false;
  relative.forEach((p, i) => {
    const n = relative[(i + 1) % relative.length]!;
    const cross = p.x * n.y - n.x * p.y; area += cross;
    if (cross > 1e-12) positive = true; if (cross < -1e-12) negative = true;
  });
  if (Math.abs(area) > 1e-12 && !(positive && negative)) return true;
  const w = circle ? 0 : volume.halfW, d = circle ? 0 : volume.halfD;
  const reach = wall.thicknessFt / 2 + (circle ? volume.r : 0) + 0.001;
  for (let i = 0; i < relative.length; i++) {
    const p = relative[i]!, n = relative[(i + 1) % relative.length]!;
    if (segmentBoxDistance(p.x, p.y, n.x - p.x, n.y - p.y, w, d).distanceSquared <= reach * reach) return true;
  }
  return false;
}

/** ft_painted_wall_wind_contact_intervals(): the merged contact intervals along a straight move of the footprint. */
export function contactIntervals(wall: WindWallGeometry, volume: Zone, to: { x: number; y: number; z: number }): Array<[number, number]> {
  const from = { x: volume.x, y: volume.y, z: volume.zMin };
  const at = (t: number) => ({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t, z: from.z + (to.z - from.z) * t });
  const intervals: Array<[number, number]> = [];
  for (let i = 1; i < wall.points.length; i++) {
    const span: WindWallGeometry = { ...wall, points: [wall.points[i - 1]!, wall.points[i]!] };
    if (!spanSweep(span, volume, to)) continue;
    let first = 0;
    if (!spanSweep(span, volume, from)) {
      let lo = 0, hi = 1;
      for (let j = 0; j < 48; j++) { const mid = (lo + hi) / 2; if (spanSweep(span, volume, at(mid))) hi = mid; else lo = mid; }
      first = hi;
    }
    const end: Zone = { ...volume, x: to.x, y: to.y, zMin: to.z, zMax: to.z + volume.zMax - volume.zMin };
    let last = 1;
    if (!spanSweep(span, end, to)) {
      let lo = 0, hi = 1;
      for (let j = 0; j < 48; j++) { const mid = (lo + hi) / 2; if (spanSweep(span, end, at(1 - mid))) hi = mid; else lo = mid; }
      last = 1 - hi;
    }
    intervals.push([first, last]);
  }
  intervals.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const interval of intervals) {
    const last = merged.length - 1;
    if (last >= 0 && interval[0] <= merged[last]![1] + 1e-12) merged[last]![1] = Math.max(merged[last]![1], interval[1]);
    else merged.push([interval[0], interval[1]]);
  }
  return merged;
}

/** ft_painted_wall_wind_first_entry(): the first genuine entry (starting inside never retriggers). */
export function firstEntry(wall: WindWallGeometry, volume: Zone, to: { x: number; y: number; z: number }):
  { t: number; exitT: number; position: { x: number; y: number; z: number } } | null {
  for (const [start, end] of contactIntervals(wall, volume, to)) {
    if (start <= 0) continue;
    return { t: start, exitT: end, position: { x: volume.x + (to.x - volume.x) * start, y: volume.y + (to.y - volume.y) * start, z: volume.zMin + (to.z - volume.zMin) * start } };
  }
  return null;
}

// ---------------------------------------------------------------------------------------------------------------
// Clear apex (painted_wall_wind_clearance.php)
// ---------------------------------------------------------------------------------------------------------------
type P2 = { x: number; y: number };
export const contourCross = (a: P2, b: P2, p: P2): number => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);

/** ft_painted_wind_clip(). */
export function clipPolygon(polygon: P2[], a: P2, b: P2, inside = true): P2[] {
  if (polygon.length < 3) return [];
  const result: P2[] = [];
  let previous = polygon[polygon.length - 1]!;
  const distance = (p: P2) => contourCross(a, b, p) * (inside ? 1 : -1);
  let pd = distance(previous);
  for (const point of polygon) {
    const d = distance(point);
    if ((pd >= 0) !== (d >= 0)) { const t = pd / (pd - d); result.push({ x: previous.x + t * (point.x - previous.x), y: previous.y + t * (point.y - previous.y) }); }
    if (d >= 0) result.push(point);
    previous = point; pd = d;
  }
  return result;
}
/** ft_painted_wind_subtract(). */
export function subtractAperture(pieces: P2[][], aperture: P2[]): P2[][] {
  if (aperture.length < 3) return pieces;
  const result: P2[][] = [];
  for (let piece of pieces) {
    for (let i = 0; i < aperture.length; i++) {
      const a = aperture[i]!, b = aperture[(i + 1) % aperture.length]!;
      const outside = clipPolygon(piece, a, b, false);
      if (outside.length >= 3) result.push(outside);
      piece = clipPolygon(piece, a, b);
      if (piece.length < 3) break;
    }
  }
  return result;
}
type ShapeBox = { id?: string; x: { min: number; max: number }; y: { min: number; max: number }; floor: number; ceiling: number;
  navigationOpenGround?: boolean; creation?: Row | null };
const boxContour = (box: ShapeBox): P2[] => [{ x: box.x.min, y: box.y.min }, { x: box.x.max, y: box.y.min }, { x: box.x.max, y: box.y.max }, { x: box.x.min, y: box.y.max }];
export function windFootprint(mover: Zone): P2[] {
  const circle = mover.kind === 'circle';
  const w = circle ? mover.r : mover.halfW, d = circle ? mover.r : mover.halfD;
  const angle = circle ? 0 : mover.facing, c = Math.cos(angle), s = Math.sin(angle);
  return [[-w, -d], [w, -d], [w, d], [-w, d]].map(([x, y]) => ({ x: mover.x + x! * c - y! * s, y: mover.y + x! * s + y! * c }));
}
/** ft_painted_wind_piece_contacts(): positive-area contact (tangency does not stop a lift). */
export function pieceContacts(polygon: P2[], mover: Zone): boolean {
  if (polygon.length < 3) return false;
  let area = 0, nearest = Infinity, inside = true;
  polygon.forEach((a, i) => {
    const b = polygon[(i + 1) % polygon.length]!;
    area += a.x * b.y - b.x * a.y;
    if (mover.kind !== 'circle') return;
    if (contourCross(a, b, mover) < 0) inside = false;
    const dx = b.x - a.x, dy = b.y - a.y, length = dx * dx + dy * dy;
    const t = length > 0 ? Math.max(0, Math.min(1, ((mover.x - a.x) * dx + (mover.y - a.y) * dy) / length)) : 0;
    nearest = Math.min(nearest, (mover.x - a.x - t * dx) ** 2 + (mover.y - a.y - t * dy) ** 2);
  });
  if (Math.abs(area) <= SHAPE_EPS) return false;
  return mover.kind !== 'circle' || inside || nearest < mover.r ** 2 - SHAPE_EPS;
}
/** ft_shape_box_is_roofed(). */
export const boxIsRoofed = (box: ShapeBox): boolean => String(box.creation?.enclosure ?? '').includes('ceiling') && box.creation?.roofRemoved !== true;

/** ft_shape_stair_contour(). */
export function stairContour(connection: Row, box: ShapeBox): P2[] {
  const axes = Array.isArray(connection.scaleXYZ) ? connection.scaleXYZ as number[] : [1, 1, 1];
  const q = Math.trunc(Number(connection.rotationQuarterTurns ?? 0));
  const anchor = connection.anchorFt as { x: number; y: number } | undefined;
  if (q < 0 || q > 3 || axes.length !== 3 || !anchor || typeof anchor !== 'object') return [];
  let points: P2[] = [];
  const footprints = ((connection.asset as Row | undefined)?.geometry as Row | undefined)?.footprints as Row | undefined;
  for (const family of ['support', 'floorCutout']) {
    for (const polygon of (Array.isArray(footprints?.[family]) ? footprints![family] as Row[] : [])) {
      for (const p of (Array.isArray(polygon.pointsM) ? polygon.pointsM as number[][] : [])) {
        const x = p[0]! * axes[0]! / 0.3048, y = p[2]! * axes[2]! / 0.3048;
        const [rx, ry] = [[x, y], [y, -x], [-x, -y], [-y, x]][q]!;
        points.push({ x: rx! + Number(anchor.x), y: ry! + Number(anchor.y) });
      }
    }
  }
  points.sort((a, b) => (a.x - b.x) || (a.y - b.y));
  const half = (list: P2[]) => { const out: P2[] = []; for (const p of list) { while (out.length >= 2 && contourCross(out[out.length - 2]!, out[out.length - 1]!, p) <= SHAPE_EPS) out.pop(); out.push(p); } return out; };
  const lower = half(points), upper = half([...points].reverse()); lower.pop(); upper.pop();
  let contour = [...lower, ...upper];
  for (const [axis, edge, sign] of [['x', box.x.min, 1], ['x', box.x.max, -1], ['y', box.y.min, 1], ['y', box.y.max, -1]] as const) {
    const input = contour; contour = [];
    if (input.length < 3) return [];
    let a = input[input.length - 1]!;
    for (const b of input) {
      const ain = (a[axis] - edge) * sign >= -SHAPE_EPS, bin = (b[axis] - edge) * sign >= -SHAPE_EPS;
      if (ain !== bin) { const t = (edge - a[axis]) / (b[axis] - a[axis]); contour.push({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }); }
      if (bin) contour.push(b);
      a = b;
    }
  }
  if (contour.length < 3) return [];
  const cx = contour.reduce((s, p) => s + p.x, 0) / contour.length, cy = contour.reduce((s, p) => s + p.y, 0) / contour.length;
  points = contour.map(p => { const dx = cx - p.x, dy = cy - p.y, d = Math.hypot(dx, dy); if (d > SHAPE_EPS) { const amount = Math.min(0.05, d * 0.1); return { x: p.x + dx / d * amount, y: p.y + dy / d * amount }; } return p; });
  return points;
}

/** ft_painted_wall_wind_clear_apex(): the highest the object can rise before a floor, a physical roof or an obstacle. */
export function clearApex(shape: { boxes?: ShapeBox[]; authoring?: Row | null }, mover: Zone, obstacles: Zone[], wanted: number,
  overlap: (a: Zone, b: Zone) => boolean): number {
  const start = mover.zMin, height = Math.max(0, mover.zMax - start);
  if (wanted <= start) return start;
  let limit = wanted;
  const sweep: Zone = { ...mover, zMax: wanted + height };
  for (const obstacle of obstacles) if (overlap(sweep, obstacle)) limit = Math.min(limit, Math.max(start, obstacle.zMin - height - 0.01));
  const footprint = windFootprint(mover);
  const boxes = shape.boxes ?? [];
  for (const box of boxes) {
    if (box.navigationOpenGround) continue;
    const surfaces = [Number(box.floor)];
    if (boxIsRoofed(box)) surfaces.push(Number(box.creation?.physicalCeilingFt ?? box.ceiling));
    surfaces.forEach((z, index) => {
      if (z <= start + SHAPE_EPS || z > limit + height) return;
      let polygon = footprint; const contour = boxContour(box);
      contour.forEach((a, i) => { polygon = clipPolygon(polygon, a, contour[(i + 1) % 4]!); });
      if (!pieceContacts(polygon, mover)) return;
      let pieces = [polygon];
      for (const other of boxes) {
        if (other === box) continue;
        const interior = !other.navigationOpenGround && z > other.floor + SHAPE_EPS && z < other.ceiling - SHAPE_EPS;
        const replacement = index > 0 && other.id !== box.id && Math.abs(other.floor - z) < 0.01;
        if (interior || replacement) pieces = subtractAperture(pieces, boxContour(other));
      }
      if (index === 0) for (const connection of (Array.isArray(shape.authoring?.verticalConnections) ? shape.authoring!.verticalConnections as Row[] : [])) {
        if ((connection.targetRoomId ?? '') === box.id) pieces = subtractAperture(pieces, stairContour(connection, box));
      }
      for (const piece of pieces) if (pieceContacts(piece, mover)) { limit = Math.min(limit, Math.max(start, z - height - 0.01)); break; }
    });
  }
  return limit;
}

// ---------------------------------------------------------------------------------------------------------------
// Plans (painted_wall_wind_lift / _settlement / _entry_plan / _drop). The world readers come from the caller.
// ---------------------------------------------------------------------------------------------------------------
type P3 = { x: number; y: number; z: number };
export interface WindWorld {
  shape: { boxes?: ShapeBox[]; authoring?: Row | null } | null;
  /** ft_ground_occupancy_shape(mover at x,y,z, support_plane_z) — the object's footprint volume. */
  footprintAt: (x: number, y: number, z: number) => Zone | null;
  /** ft_inventory_attachment() at a point (null = nothing holds it). */
  attachment: (x: number, y: number, z: number, contactAttaches: boolean) => string | null;
  /** ft_encounter_occupancy_obstacles(mover, exclude self, support_plane_z) — given the lift column's volume (only obstacles
   * overlapping that column can change the answer). */
  obstacles: (volume: Zone) => Zone[];
  overlap: (a: Zone, b: Zone) => boolean;
  /** ft_resolve_move_destination(x, apexZ, y, walk, free elevation) for the object standing at (x, y, fromZ): PLACED with no sideways nudge. */
  liftDestinationClear: (x: number, y: number, z: number, fromZ: number) => boolean;
  /** ft_inventory_rest_z() for the mover at a point. */
  restZ: (x: number, y: number, z: number, contactAttaches: boolean) => { z: number; rest: string };
}
export interface WindPlan {
  inventoryId: string; weightLb: number; from: P3; apexZ: number; wallTopZ: number; heightFt: number; to: P3; fallFt: number;
  contactT?: number; entryT?: number; movementOrigin?: P3; requestedDestination?: P3;
}

/** ft_painted_wall_wind_inventory_plan(): the lift from where the object stands. Callers checked weight / piloted. */
export function inventoryPlan(wall: WindWallGeometry, world: WindWorld, id: string, weight: number, at: P3, contactAttaches = true):
  Omit<WindPlan, 'to' | 'fallFt'> | null {
  if (!world.shape) return null;
  const volume = world.footprintAt(at.x, at.y, at.z);
  if (!volume) return null;
  if (world.attachment(volume.x, volume.y, volume.zMin, contactAttaches) !== null) return null;
  const wanted = volumeApex(wall, volume);
  if (wanted === null) return null;
  const apex = clearApex(world.shape, volume, world.obstacles(volume), wanted, world.overlap);
  if (apex <= volume.zMin + 0.001) return null;
  return { inventoryId: id, weightLb: weight, from: { x: volume.x, y: volume.y, z: volume.zMin }, apexZ: apex, wallTopZ: wanted, heightFt: volume.zMax - volume.zMin };
}

/** ft_painted_wall_wind_settlement_plan(): the whole vertical fling with its rest point. */
export function settlementPlan(wall: WindWallGeometry, world: WindWorld, id: string, weight: number, at: P3, contactAttaches = true): WindPlan | null {
  const plan = inventoryPlan(wall, world, id, weight, at, contactAttaches);
  if (!plan) return null;
  const from = plan.from;
  if (!world.liftDestinationClear(from.x, from.y, plan.apexZ, from.z)) return null;
  const rest = world.restZ(from.x, from.y, plan.apexZ, false);
  if (!['fell', 'placed'].includes(rest.rest) || rest.z > plan.apexZ + 0.001) return null;
  return { ...plan, to: { x: from.x, y: from.y, z: rest.z }, fallFt: Math.max(0, plan.apexZ - rest.z) };
}

/** ft_painted_wall_wind_entry_plan(): a loose object translated through the wall — settles from an interior witness. */
export function entryPlan(wall: WindWallGeometry, world: WindWorld, worldAt: (p: P3) => WindWorld, id: string, weight: number,
  from: P3, to: P3, contactAttaches = true): WindPlan | null {
  if (world.attachment(from.x, from.y, from.z, contactAttaches) !== null) return null;
  const volume = world.footprintAt(from.x, from.y, from.z);
  if (!volume) return null;
  const entry = firstEntry(wall, volume, to);
  if (!entry) return null;
  const distance = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
  const t = entry.t + Math.min((entry.exitT - entry.t) / 4, 0.01 / Math.max(0.01, distance));
  const contact = { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t, z: from.z + (to.z - from.z) * t };
  // The moved object is evaluated AT the contact (its footprint, obstacles and rest there), never attaching to a wall.
  const plan = settlementPlan(wall, worldAt(contact), id, weight, contact, false);
  return plan ? { ...plan, entryT: t, contactT: entry.t, movementOrigin: from, requestedDestination: to } : null;
}

/** ft_painted_wall_wind_drop_plan(): the earliest interception — movement, release, then gravity. */
export function dropPlan(sources: Array<{ anchorId: string; wall: WindWallGeometry }>, world: WindWorld, worldAt: (p: P3) => WindWorld,
  id: string, weight: number, origin: P3, requested: P3, sameWorld: boolean, rest: { z: number; rest: string }):
  { source: { anchorId: string; wall: WindWallGeometry }; plan: WindPlan; leg: number } | null {
  const candidates: Array<{ source: { anchorId: string; wall: WindWallGeometry }; plan: WindPlan; leg: number }> = [];
  for (const source of sources) {
    let plan: WindPlan | null = null, leg = 0;
    if (sameWorld) plan = entryPlan(source.wall, world, worldAt, id, weight, origin, requested);
    else {
      const released = settlementPlan(source.wall, worldAt(requested), id, weight, requested, false);
      plan = released ? { ...released, contactT: 0, entryT: 0, movementOrigin: requested, requestedDestination: requested } : null;
    }
    if (!plan && rest.rest === 'fell') {
      leg = 1;
      const landing = { ...requested, z: rest.z };
      plan = entryPlan(source.wall, worldAt(requested), worldAt, id, weight, requested, landing, sameWorld);
    }
    if (plan) candidates.push({ source, plan, leg });
  }
  candidates.sort((a, b) => (a.leg - b.leg) || ((a.plan.contactT ?? 0) - (b.plan.contactT ?? 0)) || (a.source.anchorId < b.source.anchorId ? -1 : a.source.anchorId > b.source.anchorId ? 1 : 0));
  return candidates[0] ?? null;
}
